import React, { useEffect, useMemo, useState } from "react";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import {
  useGetParcoursQuery,
  useAddParcoursMutation,
  useUpdateParcoursMutation,
  useDeleteParcoursMutation,
  useGetCategoriesQuery,
} from "../../redux/api/coachApi";

const initialForm = { name: "", description: "", categorie: 0 };

export default function ParcoursView() {
  const [form, setForm] = useState(initialForm);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  // Data
  const { data: categories = [], isLoading: isCatLoading } = useGetCategoriesQuery();
  const { data: parcours = [], isLoading, isFetching } = useGetParcoursQuery();
  
  // Mutations
  const [addParcours, { isLoading: isCreating }] = useAddParcoursMutation();
  const [updateParcours, { isLoading: isUpdating }] = useUpdateParcoursMutation();
  const [deleteParcours, { isLoading: isDeleting }] = useDeleteParcoursMutation();
  

  const isBusy = isLoading || isFetching || isCreating || isUpdating || isDeleting;

  // Validation simple
  const canSubmit = useMemo(() => {
    if (!form.name.trim()) return false;
    if (!form.categorie || Number(form.categorie) === 0) return false;
    return true;
  }, [form]);

  const resetForm = () => {
    setForm(initialForm);
    setEditing(null);
    setError("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!canSubmit) {
      setError("Veuillez remplir le nom et choisir une catégorie.");
      return;
    }

    try {
      const payload = {
        name: form.name.trim(),
        description: form.description?.trim() || "",
        categorie: Number(form.categorie),
      };

      if (editing) {
        await updateParcours({ id: editing.id, ...payload }).unwrap();
      } else {
        await addParcours(payload).unwrap();
      }
      resetForm();
    } catch (err) {
      const apiMsg =
        err?.data && typeof err.data === "object"
          ? JSON.stringify(err.data)
          : "Une erreur est survenue.";
      setError(apiMsg);
      console.error("Parcours submit error:", err);
    }
  };

  const onEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      description: item.description || "",
      // item.categorie peut être un id ou un objet { id, nom } selon ta API
      categorie: item.categorie?.id ?? item.categorie ?? 0,
    });
    setError("");
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer ce parcours ?")) return;
    try {
      await deleteParcours(id).unwrap();
    } catch (err) {
      setError("Erreur lors de la suppression.");
      console.error("Delete parcours error:", err);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-6 space-y-6">
      {/* Form Card */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-teal-700">
            {editing ? "Modifier un Parcours" : "Ajouter un Parcours"}
          </h2>
          <button
            onClick={resetForm}
            className="text-sm text-teal-600 hover:underline disabled:opacity-50"
            disabled={!editing}
            title="Réinitialiser"
          >
            {editing ? "Annuler l’édition" : ""}
          </button>
        </div>

        {error ? (
          <div className="mb-3 text-red-600 text-sm text-center">{error}</div>
        ) : null}

        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4">
          <input
            type="text"
            placeholder="Nom du parcours (ex: Développement Web)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
          />

          <textarea
            placeholder="Description (optionnel)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
          />

          <select
            value={form.categorie}
            onChange={(e) => setForm({ ...form, categorie: Number(e.target.value) })}
            className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
            disabled={isCatLoading}
          >
            <option value={0}>Sélectionner une catégorie</option>
            {categories?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nom || c.name}
              </option>
            ))}
          </select>

          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white py-2 rounded-xl transition"
            disabled={!canSubmit || isBusy}
          >
            {editing ? "Mettre à jour" : "Ajouter"}
          </button>
        </form>
      </div>

      {/* List Card */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-teal-700 mb-3">
          Liste des Parcours
        </h3>

        {isLoading ? (
          <p className="text-gray-500">Chargement…</p>
        ) : parcours && parcours.length > 0 ? (
          <ul className="space-y-3">
            {parcours.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded-xl transition"
              >
                <div className="flex flex-col">
                  <span className="font-medium text-gray-800">{p.name}</span>
                  <span className="text-xs text-gray-500">
                    Catégorie : {p.categorie?.nom || p.categorie || "—"}
                  </span>
                  {p.description ? (
                    <span className="text-xs text-gray-500 line-clamp-2">
                      {p.description}
                    </span>
                  ) : null}
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => onEdit(p)}
                    className="p-2 rounded-lg hover:bg-white text-blue-600"
                    title="Modifier"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    onClick={() => onDelete(p.id)}
                    className="p-2 rounded-lg hover:bg-white text-red-600"
                    title="Supprimer"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500 text-center">Aucun parcours trouvé.</p>
        )}
      </div>
    </div>
  );
}
